import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Typography from '@mui/joy/Typography';
import Button from '@mui/joy/Button';
import Box from '@mui/joy/Box';
import type { SerializedError } from '@reduxjs/toolkit';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import AuthLayout from '../../components/AuthLayout/AuthLayout';
import Athenalogo from '../../assets/images/Athena_logo_final.png';
import { useConfirmAccountMutation, useLazyValidateConfirmationTokenQuery } from '../../services/api.user';
import { isFetchBaseQueryError } from '../../utils/isFetchBaseQueryError';
import { useAuth } from '../../hooks/useAuth';

const invalidLinkMessage = 'This confirmation link is invalid or has expired.';
const genericErrorMessage = 'Something went wrong. Please try again.';

type ApiError = FetchBaseQueryError | SerializedError | undefined;

function getErrorMessage(error: ApiError, fallback = genericErrorMessage) {
  return isFetchBaseQueryError(error) ? error.data?.message ?? fallback : fallback;
}

export default function ConfirmAccount() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [validateConfirmationToken, { isFetching: isValidating }] = useLazyValidateConfirmationTokenQuery();
  const [confirmAccount, { isLoading: isConfirming }] = useConfirmAccountMutation();
  const [isTokenValid, setIsTokenValid] = useState(false);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const hasConfirmed = useRef(false);

  useEffect(() => {
    let ignoreValidationResult = false;

    async function validateToken() {
      setIsTokenValid(false);
      setIsConfirmed(false);
      setErrorMessage(undefined);
      hasConfirmed.current = false;

      if (!token) {
        setErrorMessage(invalidLinkMessage);
        return;
      }

      try {
        await validateConfirmationToken(token).unwrap();
        if (ignoreValidationResult) return;
        setIsTokenValid(true);
      } catch (error) {
        if (ignoreValidationResult) return;
        setErrorMessage(getErrorMessage(error as ApiError, invalidLinkMessage));
      }
    }

    validateToken();

    return () => {
      ignoreValidationResult = true;
    };
  }, [token, validateConfirmationToken]);

  const handleConfirmAccount = async () => {
    if (hasConfirmed.current) return;

    if (!token) {
      setErrorMessage(invalidLinkMessage);
      return;
    }

    hasConfirmed.current = true;
    setErrorMessage(undefined);

    try {
      await confirmAccount(token).unwrap();
      setIsTokenValid(false);
      setIsConfirmed(true);
    } catch (error) {
      hasConfirmed.current = false;
      setErrorMessage(getErrorMessage(error as ApiError));
    }
  };

  const handleNavigate = () => {
    navigate(isAuthenticated ? '/' : '/login');
  };

  return (
    <AuthLayout>
      <img
        src={Athenalogo}
        alt="logo"
        style={{
          width: '40%',
          padding: '8% 0 0 0'
        }}
      />

      {isValidating && !isConfirmed && !errorMessage && (
        <Typography level="body-lg" marginTop={2}>
          Checking confirmation link...
        </Typography>
      )}

      {isConfirming && !isConfirmed && !errorMessage && (
        <Typography level="body-lg" marginTop={2}>
          Confirming your account...
        </Typography>
      )}

      {isTokenValid && !isConfirming && !isConfirmed && !errorMessage && (
        <>
          <Typography level="body-lg" marginTop={2}>
            Please verify your account to keep access.
          </Typography>
          <Box marginTop={2} marginBottom={2}>
            <Button onClick={handleConfirmAccount} size="lg">
              VERIFY ACCOUNT
            </Button>
          </Box>
        </>
      )}

      {isConfirmed && (
        <>
          <Typography level="body-lg" marginTop={2} color="success">
            Account confirmed successfully!
          </Typography>
          <Box marginTop={2} marginBottom={2}>
            <Button onClick={handleNavigate} size="lg">
              {isAuthenticated ? 'GO TO ATHENA' : 'LOG IN'}
            </Button>
          </Box>
        </>
      )}

      {errorMessage && (
        <>
          <Typography level="body-lg" marginTop={2} color="danger">
            {errorMessage}
          </Typography>
          <Box marginTop={2} marginBottom={2}>
            <Button variant="outlined" onClick={handleNavigate} size="lg">
              {isAuthenticated ? 'GO TO ATHENA' : 'LOG IN'}
            </Button>
          </Box>
        </>
      )}
    </AuthLayout>
  );
}
